import { withMutations, withResource } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { createRxMutation, createRxResource } from '@next/core-lib/notification';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { Admission, Ingredient, UpsertResponse } from '../models/crop-aid-admission.models';
import { CropAidAdmissionService } from '../services/crop-aid-admission.service';

interface CropAidAdmissionState {
  admissionFilter: string;
  ingredientFilter: string;
  selectedAdmission: Admission | undefined;
  selectedIngredient: Ingredient | undefined;
}

const initialState: CropAidAdmissionState = {
  admissionFilter: '',
  ingredientFilter: '',
  selectedAdmission: undefined,
  selectedIngredient: undefined,
};

export const CropAidAdmissionStore = signalStore(
  withState(initialState),
  withResource(
    () => {
      const api = inject(CropAidAdmissionService);

      return {
        admissions: createRxResource<Admission[], void>({
          stream: () => api.getAdmissions(),
          defaultValue: [],
          errorMessage: 'cropAidAdmission.errors.loadAdmissions',
        }),
        ingredients: createRxResource<Ingredient[], void>({
          stream: () => api.getIngredients(),
          defaultValue: [],
          errorMessage: 'cropAidAdmission.errors.loadIngredients',
        }),
      };
    },
    { errorHandling: 'previous value' },
  ),
  withMutations((store) => {
    const api = inject(CropAidAdmissionService);

    return {
      upsertAdmission: createRxMutation<Admission, UpsertResponse>({
        operation: (admission) => api.upsertAdmission(admission),
        notification: {
          successMessage: 'cropAidAdmission.messages.saveAdmissionSuccess',
          errorMessage: 'cropAidAdmission.errors.saveAdmission',
        },
        onSuccess: () => store._admissionsReload(),
      }),
      upsertIngredient: createRxMutation<Ingredient, UpsertResponse>({
        operation: (ingredient) => api.upsertIngredient(ingredient),
        notification: {
          successMessage: 'cropAidAdmission.messages.saveIngredientSuccess',
          errorMessage: 'cropAidAdmission.errors.saveIngredient',
        },
        onSuccess: () => store._ingredientsReload(),
      }),
    };
  }),
  withComputed((store) => ({
    sortedAdmissions: computed(() => [...store.admissionsValue()].sort((left, right) => left.number.localeCompare(right.number))),
    sortedIngredients: computed(() =>
      [...store.ingredientsValue()].sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })),
    ),
    admissionsLoading: computed(() => store.admissionsIsLoading() || store.upsertAdmissionIsPending()),
    ingredientsLoading: computed(() => store.ingredientsIsLoading() || store.upsertIngredientIsPending()),
    admissionsLoadFailed: computed(() => store.admissionsError() !== undefined),
    ingredientsLoadFailed: computed(() => store.ingredientsError() !== undefined),
  })),
  withMethods((store) => ({
    loadAdmissions: () => store._admissionsReload(),
    loadIngredients: () => store._ingredientsReload(),
    setAdmissionFilter: (admissionFilter: string) => patchState(store, { admissionFilter }),
    setIngredientFilter: (ingredientFilter: string) => patchState(store, { ingredientFilter }),
    selectAdmission: (selectedAdmission: Admission | undefined) => patchState(store, { selectedAdmission }),
    selectIngredient: (selectedIngredient: Ingredient | undefined) => patchState(store, { selectedIngredient }),
  })),
);
